/**
 * useBulkPromotionAppointments Hook (V73 수정)
 *
 * 진급 후보 리스트에서 여러 명의 승진 발령을 일괄 처리하는 Hook입니다.
 *
 * **핵심 기능:**
 * - 진급 후보(PromotionCandidate[])를 받아 personnelAppointments 일괄 생성
 * - V73: 백엔드 saveEarlyPromotionFunc 사용 (V72 필드 완전 지원)
 * - appointmentType: 'EARLY_PROMOTION', status: 'PLANNED/EXECUTED' 자동 설정
 * - Employee.positionId/currentPositionStartDate 자동 동기화
 *
 * **비즈니스 플로우:**
 * 1. HR이 진급 후보 페이지에서 대상자 선택
 * 2. "진급 발령 생성" 버튼 클릭
 * 3. BulkPromotionAppointmentModal에서 공통 발령일/메모 입력
 * 4. 이 Hook의 createPromotionAppointmentsFromCandidates 호출
 * 5. 각 후보에 대해 백엔드 Callable Function 호출
 * 6. 백엔드에서 V72 필드 포함 personnelAppointments 문서 생성
 * 7. 각 Employee의 position 필드 자동 업데이트
 *
 * **사용 예시:**
 * ```tsx
 * const { createPromotionAppointmentsFromCandidates, isSubmitting, error } =
 *   useBulkPromotionAppointments();
 *
 * await createPromotionAppointmentsFromCandidates(selectedCandidates, {
 *   effectiveDate: '2025-01-01',
 *   note: '2025년 정기 인사발령'
 * });
 * ```
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { PromotionCandidate } from '../types/promotionCandidate';

/**
 * V73: 조기진급 저장 요청 파라미터
 */
interface SaveEarlyPromotionRequest {
  employeeId: string;
  fromPositionId: string;
  toPositionId: string;
  effectiveDate: string;
  promotionBonusYearsToAdd?: number;
  reason?: string;
}

/**
 * V73: 조기진급 저장 응답
 */
interface SaveEarlyPromotionResponse {
  success: boolean;
  message: string;
  appointmentId: string;
  isExecuted: boolean;
}

/**
 * useBulkPromotionAppointments Hook 반환 타입
 */
export interface UseBulkPromotionAppointmentsResult {
  /**
   * 진급 후보를 일괄 승진 발령 처리
   *
   * @param candidates 진급 후보 목록
   * @param options 발령 옵션 (발령일, 메모)
   */
  createPromotionAppointmentsFromCandidates: (
    candidates: PromotionCandidate[],
    options: {
      /** 발령일 (YYYY-MM-DD) */
      effectiveDate: string;
      /** 공통 메모 (선택) */
      note?: string;
    }
  ) => Promise<BulkAppointmentResult>;

  /** 제출 중 여부 */
  isSubmitting: boolean;

  /** 에러 */
  error: Error | null;
}

/**
 * 일괄 발령 결과
 */
export interface BulkAppointmentResult {
  /** 성공한 발령 수 */
  successCount: number;

  /** 실패한 발령 수 */
  failureCount: number;

  /** 전체 발령 수 */
  totalCount: number;

  /** 실패 목록 (에러 메시지 포함) */
  failures: Array<{
    employeeId: string;
    employeeName: string;
    error: string;
  }>;
}

/**
 * 일괄 승진 발령 처리 Hook (V73)
 *
 * @returns UseBulkPromotionAppointmentsResult
 */
export function useBulkPromotionAppointments(): UseBulkPromotionAppointmentsResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 진급 후보를 일괄 승진 발령 처리 (V73 수정)
   *
   * **중요한 비즈니스 로직:**
   *
   * 이 함수는 진급 후보 리스트에서 실제 승진 발령(personnelAppointments)을 생성하는 역할을 합니다.
   *
   * **처리 과정 (V73 개선):**
   * 1. 각 진급 후보(PromotionCandidate)를 순회
   * 2. 백엔드 saveEarlyPromotionFunc Callable Function 호출
   * 3. 백엔드에서 V72 필드 포함 personnelAppointments 문서 생성
   * 4. 백엔드에서 권한 검증, 날짜 검증, 중복 예약 방지 수행
   * 5. 백엔드에서 Employee.positionId/currentPositionStartDate 자동 동기화
   * 6. 성공/실패 결과 수집 및 반환
   *
   * **동기화 메커니즘:**
   * - saveEarlyPromotionFunc가 effectiveDate <= today인 경우 즉시 실행 (EXECUTED)
   * - effectiveDate > today인 경우 예약 (PLANNED)
   * - EXECUTED인 경우 users 문서의 positionId, currentPositionStartDate 즉시 업데이트
   * - PLANNED인 경우 personnelAppointmentScheduler가 발령일에 자동 실행
   *
   * **트랜잭션/배치 처리 (TODO):**
   * - 현재는 각 발령을 개별 Promise로 처리합니다.
   * - 일부 실패하더라도 나머지는 계속 진행합니다 (부분 성공 허용).
   * - 향후 Firestore Batch Write로 "모두 성공 또는 모두 실패" 트랜잭션 처리 가능.
   *
   * @param candidates 진급 후보 목록
   * @param options 발령 옵션
   * @returns 일괄 발령 결과
   */
  const createPromotionAppointmentsFromCandidates = async (
    candidates: PromotionCandidate[],
    options: {
      effectiveDate: string;
      note?: string;
    }
  ): Promise<BulkAppointmentResult> => {
    setIsSubmitting(true);
    setError(null);

    const failures: BulkAppointmentResult['failures'] = [];
    let successCount = 0;

    try {
      // Firebase가 초기화되지 않은 경우
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다. 환경 변수를 확인해주세요.');
      }

      // 백엔드 Callable Function 준비
      const saveEarlyPromotionFunc = httpsCallable<
        SaveEarlyPromotionRequest,
        SaveEarlyPromotionResponse
      >(functions, 'saveEarlyPromotion');

      // 각 후보에 대해 승진 발령 생성
      for (const candidate of candidates) {
        try {
          // 필수 데이터 검증
          if (!candidate.nextPosition) {
            throw new Error('다음 직위 정보가 없습니다 (최고 직위일 수 있습니다).');
          }

          const result = await saveEarlyPromotionFunc({
            employeeId: candidate.employee.id,
            fromPositionId: candidate.currentPosition.id,
            toPositionId: candidate.nextPosition.id,
            effectiveDate: options.effectiveDate,
            promotionBonusYearsToAdd: candidate.recognizedYearsBreakdown?.promotionBonusYears || 0,
            reason: options.note,
          });

          // 성공 카운트 증가
          successCount++;

        } catch (err: any) {
          // 개별 발령 실패 시 기록하고 계속 진행
          console.error(
            `❌ [V73] 진급 발령 생성 실패: ${candidate.employee.nameKo}`,
            err
          );

          // V74: Firebase Functions 에러 메시지 추출 (failed-precondition 추가)
          let errorMessage = '알 수 없는 오류';
          if (err.code === 'functions/failed-precondition') {
            // V74: 진급 예약 중복 시 반환되는 에러
            errorMessage = err.message || '이미 진급 예약이 존재합니다. 기존 예약 취소 후 진행하세요.';
          } else if (err.code === 'functions/already-exists') {
            errorMessage = err.message || '이미 예약된 진급 발령이 있습니다.';
          } else if (err.code === 'functions/invalid-argument') {
            errorMessage = err.message || '입력값이 올바르지 않습니다.';
          } else if (err.code === 'functions/permission-denied') {
            errorMessage = '인사팀 권한이 필요합니다.';
          } else if (err.code === 'functions/not-found') {
            errorMessage = '해당 직원을 찾을 수 없습니다.';
          } else if (err.message) {
            errorMessage = err.message;
          }

          failures.push({
            employeeId: candidate.employee.id,
            employeeName: candidate.employee.nameKo,
            error: errorMessage,
          });
        }
      }

      // 결과 반환
      const result: BulkAppointmentResult = {
        successCount,
        failureCount: failures.length,
        totalCount: candidates.length,
        failures,
      };

      setIsSubmitting(false);

      // 전체 실패 시 에러로 처리
      if (successCount === 0 && failures.length > 0) {
        const errorMessage = `전체 ${failures.length}건의 발령이 모두 실패했습니다.`;
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      }

      return result;
    } catch (err: any) {
      setIsSubmitting(false);
      setError(err);
      throw err;
    }
  };

  return {
    createPromotionAppointmentsFromCandidates,
    isSubmitting,
    error,
  };
}

/**
 * ============================================
 * TODO: 트랜잭션/배치 처리 (향후 구현)
 * ============================================
 *
 * 현재는 각 발령을 개별 Promise로 처리하므로, 일부만 성공하는 "부분 성공" 상황이 발생할 수 있습니다.
 *
 * **문제점:**
 * - 10명 중 5명만 성공하면 나머지 5명은 수동으로 재처리 필요
 * - 데이터 일관성 문제 발생 가능
 *
 * **개선 방안: Firestore Batch Write**
 *
 * ```ts
 * import { writeBatch } from 'firebase/firestore';
 *
 * async function createPromotionAppointmentsWithBatch(
 *   candidates: PromotionCandidate[],
 *   options: { effectiveDate: string; note?: string }
 * ): Promise<void> {
 *   if (!db) throw new Error('Firestore가 초기화되지 않았습니다.');
 *
 *   // Firestore Batch (최대 500건)
 *   const batch = writeBatch(db);
 *
 *   // 각 후보에 대해 발령 문서 생성
 *   for (const candidate of candidates) {
 *     const appointmentId = generateAppointmentId(candidate.employee.id);
 *     const docRef = doc(db, 'personnelAppointments', appointmentId);
 *
 *     batch.set(docRef, {
 *       employeeId: candidate.employee.id,
 *       fromPositionId: candidate.currentPosition.id,
 *       toPositionId: candidate.nextPosition.id,
 *       effectiveDate: options.effectiveDate,
 *       type: 'promotion',
 *       note: options.note,
 *       createdAt: serverTimestamp(),
 *       updatedAt: serverTimestamp(),
 *       createdBy: authContext.userId,
 *     });
 *
 *     // Employee 문서도 함께 업데이트
 *     const employeeDocRef = doc(db, 'users', candidate.employee.id);
 *     batch.update(employeeDocRef, {
 *       positionId: candidate.nextPosition.id,
 *       currentPositionStartDate: options.effectiveDate,
 *       updatedAt: serverTimestamp(),
 *     });
 *   }
 *
 *   // 일괄 커밋 (모두 성공 또는 모두 실패)
 *   await batch.commit();
 * }
 * ```
 *
 * **주의사항:**
 * - Firestore Batch는 최대 500건까지만 지원
 * - 500건 초과 시 여러 Batch로 분할 필요
 * - Employee 동기화 로직이 중복되므로, 현재 usePersonnelAppointmentMutations의
 *   syncEmployeeCurrentPosition 로직과 일치시켜야 함
 *
 * **권장:**
 * - 정기 인사발령 규모가 500명 미만이면 Batch 사용 고려
 * - 500명 초과 시 현재 방식(개별 처리) 유지하되, 재시도 메커니즘 추가
 */

/**
 * ============================================
 * TODO: 발령 승인 Workflow 연동 (향후 구현)
 * ============================================
 *
 * 현재는 발령 생성 즉시 반영됩니다.
 * 향후 승인 프로세스를 추가할 경우:
 *
 * ```ts
 * // 발령 생성 시 status='pending'
 * await createAppointment({
 *   ...
 *   status: 'pending', // 대기 상태
 * });
 *
 * // 승인자가 별도로 승인 처리
 * await approveAppointment(appointmentId);
 * // → status='approved'로 변경
 * // → Employee 동기화
 * ```
 *
 * **장점:**
 * - HR이 발령 내역을 사전 검토 가능
 * - 실수로 잘못된 발령 방지
 * - 승인 이력 추적 가능
 *
 * **단점:**
 * - 추가 작업 단계 발생
 * - 승인자 부재 시 발령 지연
 */

/**
 * ============================================
 * TODO: 진급 발령 취소/Rollback (향후 구현)
 * ============================================
 *
 * 잘못 생성된 발령을 되돌리는 기능:
 *
 * ```ts
 * async function rollbackPromotionAppointments(
 *   appointmentIds: string[]
 * ): Promise<void> {
 *   // 1. 각 발령 삭제
 *   for (const id of appointmentIds) {
 *     await deleteAppointment(id);
 *     // → usePersonnelAppointmentMutations 내부에서 Employee 재동기화
 *   }
 *
 *   // 2. Employee의 currentPosition*는 자동으로 이전 발령으로 롤백됨
 *   // (가장 최신 발령 = 직전 발령)
 * }
 * ```
 *
 * **권장:**
 * - 발령 삭제는 매우 신중하게 처리 (감사 로그 필수)
 * - SuperAdmin만 권한 부여
 * - 삭제 전 확인 다이얼로그 표시
 */

/**
 * ============================================
 * TODO: 외부 알림 (Slack, 메일 등)
 * ============================================
 *
 * 발령 생성 완료 시 알림 발송:
 *
 * ```ts
 * // 1. Slack 알림
 * await sendSlackNotification({
 *   channel: '#hr-notifications',
 *   text: `[인사발령] 총 ${successCount}명의 승진 발령이 생성되었습니다.`
 * });
 *
 * // 2. 이메일 알림 (대상자에게 개별 발송)
 * for (const candidate of candidates) {
 *   await sendEmail({
 *     to: candidate.employee.email,
 *     subject: '[인사발령] 승진 축하드립니다',
 *     body: `${candidate.employee.nameKo}님,
 *            ${candidate.currentPosition.name}에서 ${candidate.nextPosition.name}으로 승진하셨습니다.
 *            발령일: ${options.effectiveDate}`
 *   });
 * }
 * ```
 *
 * **구현 방법:**
 * - Firebase Functions (백엔드 트리거)
 * - 또는 프론트엔드에서 별도 API 호출
 */
